<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  selectedContact: {
    type: Object,
    required: true,
  },
  popover: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();

const contactName = computed(
  () =>
    props.selectedContact?.name ||
    props.selectedContact?.email ||
    props.selectedContact?.phoneNumber ||
    t('CONTACTS_LAYOUT.AI_SUMMARY.CLIENT_FALLBACK')
);

const additionalAttributes = computed(
  () => props.selectedContact?.additionalAttributes || {}
);

const preferredChannel = computed(
  () =>
    additionalAttributes.value.preferredChannel ||
    additionalAttributes.value.preferred_channel ||
    t('CONTACTS_LAYOUT.AI_SUMMARY.DEFAULT_CHANNEL')
);

const summaryItems = computed(() => [
  t('CONTACTS_LAYOUT.AI_SUMMARY.POINTS.INTEREST'),
  t('CONTACTS_LAYOUT.AI_SUMMARY.POINTS.TIMING'),
  t('CONTACTS_LAYOUT.AI_SUMMARY.POINTS.NEXT_ACTION', {
    channel: preferredChannel.value,
  }),
]);

const rootClass = computed(() =>
  props.popover
    ? 'rounded-xl border border-n-violet-6 bg-n-violet-2'
    : 'border-y border-n-violet-5 bg-n-violet-2'
);
</script>

<template>
  <section class="w-full px-4 py-4" :class="rootClass">
    <div class="mb-3 flex items-start justify-between gap-3">
      <div class="flex min-w-0 items-center gap-2">
        <span
          class="flex size-8 shrink-0 items-center justify-center rounded-lg bg-n-violet-9 text-white shadow-sm"
        >
          <span class="i-lucide-sparkles size-4" />
        </span>
        <div class="min-w-0">
          <h2 class="mb-0 truncate text-sm font-semibold text-n-slate-12">
            {{ t('CONTACTS_LAYOUT.AI_SUMMARY.TITLE') }}
          </h2>
          <p class="mb-0 truncate text-xs text-n-slate-10">
            {{
              t('CONTACTS_LAYOUT.AI_SUMMARY.SUBTITLE', { name: contactName })
            }}
          </p>
        </div>
      </div>
      <span
        class="shrink-0 rounded-full bg-white/60 px-2 py-1 text-[11px] font-semibold text-n-violet-11 dark:bg-n-violet-3"
      >
        {{ t('CONTACTS_LAYOUT.AI_SUMMARY.MOCK_BADGE') }}
      </span>
    </div>

    <ul class="mb-0 flex flex-col gap-2">
      <li
        v-for="item in summaryItems"
        :key="item"
        class="flex items-start gap-2 text-sm leading-5 text-n-slate-12"
      >
        <span class="mt-2 size-1.5 shrink-0 rounded-full bg-n-violet-9" />
        <span>{{ item }}</span>
      </li>
    </ul>
  </section>
</template>
